
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { CheckCircle, Mail, Shield, Gift } from 'lucide-react';

interface SignupFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  profession: string;
  hearAbout: string;
  emailConsent: boolean;
  smsConsent: boolean;
  termsAccept: boolean;
}

const SignupSection: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset } = useForm<SignupFormData>();

  const onSubmit = (data: SignupFormData) => {
    console.log('Form submitted:', data);
    
    // Create mailto link with form data
    const subject = encodeURIComponent('New Discount Pro Signup');
    const body = encodeURIComponent(`
New signup from Discount Pro website:

Name: ${data.firstName} ${data.lastName}
Email: ${data.email}
Phone: ${data.phone || 'Not provided'}
Profession: ${data.profession || 'Not provided'}
How they heard about us: ${data.hearAbout}

Consent:
- Email updates: ${data.emailConsent ? 'Yes' : 'No'}
- SMS updates: ${data.smsConsent ? 'Yes' : 'No'}
- Terms accepted: ${data.termsAccept ? 'Yes' : 'No'}

Submitted at: ${new Date().toLocaleString()}
    `);
    
    // Open email client with pre-filled data
    window.location.href = `mailto:help@discountpro.org?subject=${subject}&body=${body}`;
    
    setIsSubmitted(true);
    reset();
    setTimeout(() => setIsSubmitted(false), 5000);
  };

  const hearAboutOptions = [
    'Google Search',
    'Social Media',
    'Friend Referral',
    'Online Advertisement',
    'Email Newsletter',
    'Blog/Website',
    'Other'
  ];

  if (isSubmitted) {
    return (
      <section id="signup" className="py-20 bg-green-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Welcome to Discount Pro!</h2>
            <p className="text-xl text-gray-600 mb-6">
              Thank you for joining our community of smart savers. You'll receive your first exclusive deals within 24 hours.
            </p>
            <p className="text-gray-500">
              Check your email for a confirmation message and add help@discountpro.org to your contacts 
              to ensure you never miss a deal.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="signup" className="py-20 bg-green-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Join the Discount Pro Community
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get exclusive access to verified deals, coupons, and money-saving tips delivered right to your inbox.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Benefits Section */}
            <div className="bg-green-600 text-white p-8">
              <h3 className="text-2xl font-bold mb-6">What You'll Get:</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Gift className="w-6 h-6 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold">Daily Deal Alerts</h4>
                    <p className="text-green-100">Exclusive discounts delivered to your inbox every morning</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Mail className="w-6 h-6 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold">Weekly Savings Report</h4>
                    <p className="text-green-100">Track how much you've saved with our deals</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Shield className="w-6 h-6 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold">Verified & Tested</h4>
                    <p className="text-green-100">Every deal is verified before we send it to you</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 p-4 bg-green-700 rounded-lg">
                <p className="text-sm text-green-100">
                  <strong>Privacy Promise:</strong> We never sell your data. You can unsubscribe anytime with one click.
                  We're CAN-SPAM compliant and respect your privacy.
                </p>
              </div>
            </div>

            {/* Form Section */}
            <div className="p-8">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      {...register('firstName', { required: 'First name is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500"
                      placeholder="John"
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-sm mt-1">{errors.firstName.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      {...register('lastName', { required: 'Last name is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500"
                      placeholder="Doe"
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-sm mt-1">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500"
                    placeholder="john@example.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number (Optional)
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    {...register('phone')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500"
                    placeholder="(555) 123-4567"
                  />
                  <p className="text-xs text-gray-500 mt-1">For exclusive SMS deals (optional)</p>
                </div>

                <div>
                  <label htmlFor="profession" className="block text-sm font-medium text-gray-700 mb-1">
                    Profession (Optional)
                  </label>
                  <input
                    type="text"
                    id="profession"
                    {...register('profession')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500"
                    placeholder="Software Engineer, Teacher, etc."
                  />
                </div>

                <div>
                  <label htmlFor="hearAbout" className="block text-sm font-medium text-gray-700 mb-1">
                    How did you hear about us? *
                  </label>
                  <select
                    id="hearAbout"
                    {...register('hearAbout', { required: 'Please select an option' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Select an option</option>
                    {hearAboutOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  {errors.hearAbout && (
                    <p className="text-red-500 text-sm mt-1">{errors.hearAbout.message}</p>
                  )}
                </div>

                {/* Consent Checkboxes */}
                <div className="space-y-3 border-t pt-6">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="emailConsent"
                      {...register('emailConsent', { required: 'Email consent is required' })}
                      className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <label htmlFor="emailConsent" className="text-sm text-gray-700">
                      <strong>Yes, I want to receive email updates</strong> about exclusive deals, discounts, 
                      and money-saving tips from Discount Pro. I understand I can unsubscribe at any time. *
                    </label>
                  </div>
                  {errors.emailConsent && (
                    <p className="text-red-500 text-sm">{errors.emailConsent.message}</p>
                  )}

                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="smsConsent"
                      {...register('smsConsent')}
                      className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <label htmlFor="smsConsent" className="text-sm text-gray-700">
                      I also want to receive SMS text messages about time-sensitive deals (optional). 
                      Message and data rates may apply.
                    </label>
                  </div>

                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="termsAccept"
                      {...register('termsAccept', { required: 'You must accept the terms' })}
                      className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <label htmlFor="termsAccept" className="text-sm text-gray-700">
                      I agree to the <button type="button" className="text-green-600 underline hover:text-green-700">Terms of Service</button> and{' '}
                      <button type="button" className="text-green-600 underline hover:text-green-700">Privacy Policy</button> *
                    </label>
                  </div>
                  {errors.termsAccept && (
                    <p className="text-red-500 text-sm">{errors.termsAccept.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full bg-green-600 text-white py-4 rounded-lg text-lg font-semibold hover:bg-green-700 transition-colors"
                >
                  Join Free & Start Saving Today
                </button>

                <p className="text-xs text-gray-500 text-center">
                  By submitting this form, you acknowledge that the information you provide will be processed 
                  in accordance with our Privacy Policy. We are committed to protecting your privacy and will never 
                  sell your personal information to third parties.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SignupSection;
